import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Locale } from '@/services/localeService';
import localeService from '@/services/localeService';
import { useSettingspageTranslations } from '@/hooks/useSettingspageTranslations';

interface ManageLocalesModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (locale: Locale) => void;
}

export function ManageLocalesModal({ open, onClose, onSuccess }: ManageLocalesModalProps) {
  const [isCreating, setIsCreating] = useState(false);
  const queryClient = useQueryClient();
  const i18n = useSettingspageTranslations();

  // Add strict locale code pattern: two lowercase letters, underscore, two uppercase letters (e.g., en_US)
  const localeCodePattern = /^[a-z]{2}_[A-Z]{2}$/;

  const localeSchema = z.object({
    code: z
      .string()
      .regex(localeCodePattern, i18n.organization.manageLocales.validation.codeInvalidFormat)
      .min(2, i18n.organization.manageLocales.validation.codeMinLength)
      .max(10, i18n.organization.manageLocales.validation.codeMaxLength),
    label: z.string().min(1, i18n.organization.manageLocales.validation.labelRequired),
  });

  type LocaleFormValues = z.infer<typeof localeSchema>;

  const form = useForm<LocaleFormValues>({
    resolver: zodResolver(localeSchema),
    defaultValues: {
      code: '',
      label: '',
    },
  });

  // Fetch organization locales
  const {
    data: locales = [],
    isLoading,
    refetch,
  } = useQuery<Locale[]>({
    queryKey: ['locales'],
    queryFn: async () => {
      return await localeService.getLocales();
    },
    enabled: open,
  });

  // Create locale mutation
  const { mutate: createLocale, isPending: isCreatingLocale } = useMutation({
    mutationFn: async (data: LocaleFormValues) => {
      return await localeService.createLocale(data.code, data.label);
    },
    onSuccess: newLocale => {
      toast.success(i18n.organization.manageLocales.createSuccess);
      form.reset();
      setIsCreating(false);
      refetch();
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['locales'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      onSuccess?.(newLocale);
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.code?.[0] ||
        i18n.organization.manageLocales.createError;
      toast.error(message);
    },
  });

  // Delete locale mutation
  const { mutate: deleteLocale } = useMutation({
    mutationFn: async (localeId: number) => {
      await localeService.deleteLocale(localeId);
    },
    onSuccess: () => {
      toast.success(i18n.organization.manageLocales.deleteSuccess);
      refetch();
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['locales'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      // Trigger parent component refetch
      onSuccess?.(null as any); // This will trigger useOrgSettings refetch
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || i18n.organization.manageLocales.deleteError;
      toast.error(message);
    },
  });

  const handleSubmit = (data: LocaleFormValues) => {
    createLocale(data);
  };

  const handleDelete = (locale: Locale) => {
    const confirmMessage = i18n.organization.manageLocales.deleteConfirm
      .replace('{{label}}', locale.label)
      .replace('{{code}}', locale.code);

    if (confirm(confirmMessage)) {
      deleteLocale(locale.id);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[80vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{i18n.organization.manageLocales.title}</DialogTitle>
          <DialogDescription>{i18n.organization.manageLocales.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Create Locale Section */}
          <div className="rounded-lg border p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium">{i18n.organization.manageLocales.addNewTitle}</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsCreating(!isCreating)}
              >
                <Plus className="mr-2 size-4" />
                {isCreating
                  ? i18n.organization.manageLocales.cancelButton
                  : i18n.organization.manageLocales.addLocaleButton}
              </Button>
            </div>

            {isCreating && (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{i18n.organization.manageLocales.codeLabel}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={i18n.organization.manageLocales.codePlaceholder}
                              maxLength={10}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="label"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{i18n.organization.manageLocales.labelLabel}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={i18n.organization.manageLocales.labelPlaceholder}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={isCreatingLocale}>
                      {isCreatingLocale && <Loader2 className="mr-2 size-4 animate-spin" />}
                      {i18n.organization.manageLocales.createButton}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setIsCreating(false)}>
                      {i18n.organization.manageLocales.cancelButton}
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </div>

          {/* Locales List */}
          <div className="rounded-lg border">
            <div className="border-b p-4">
              <h3 className="text-lg font-medium">
                {i18n.organization.manageLocales.organizationLocalesTitle}
              </h3>
              <p className="text-sm text-muted-foreground">
                {i18n.organization.manageLocales.organizationLocalesDescription}
              </p>
            </div>

            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="mx-auto size-8 animate-spin" />
                <p className="mt-2 text-sm text-muted-foreground">
                  {i18n.organization.manageLocales.loadingMessage}
                </p>
              </div>
            ) : locales.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-muted-foreground">
                  {i18n.organization.manageLocales.noLocalesMessage}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{i18n.organization.manageLocales.codeHeader}</TableHead>
                    <TableHead>{i18n.organization.manageLocales.labelHeader}</TableHead>
                    <TableHead>{i18n.organization.manageLocales.statusHeader}</TableHead>
                    <TableHead className="text-right">
                      {i18n.organization.manageLocales.actionsHeader}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {locales.map(locale => (
                    <TableRow key={locale.id}>
                      <TableCell>
                        <Badge variant="outline">{locale.code}</Badge>
                      </TableCell>
                      <TableCell>{locale.label}</TableCell>
                      <TableCell>
                        <Badge variant={locale.is_active ? 'default' : 'secondary'}>
                          {locale.is_active
                            ? i18n.organization.manageLocales.activeStatus
                            : i18n.organization.manageLocales.inactiveStatus}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(locale)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {i18n.organization.manageLocales.closeButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
